import React from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { PageHero } from '../components/ui/PageHero';
import { SectionHeading } from '../components/ui/SectionHeading';
import { PrinciplesList } from '../components/ui/PrinciplesList';
import { ProcessSteps } from '../components/ui/ProcessSteps';
import { CallToAction } from '../components/ui/CallToAction';
import { images } from '../data/images';
import { values } from '../data/approach';
const philosophy = [{
  title: 'Context before candidates',
  text: 'A role only makes sense within its organization. We invest time in understanding strategy, culture, team dynamics and the specific problem an appointment is meant to solve before we consider who might fill it.'
}, {
  title: 'Research, not reliance on networks',
  text: 'Networks are useful, but they are inherently limited. Structured research lets us look across the full relevant market — including people who are not actively seeking a move.'
}, {
  title: 'Fit is always specific',
  text: 'The strongest candidate on paper is not always the right appointment. We assess against criteria agreed for the particular role, organization and moment.'
}];
export function About() {
  usePageMeta('About Us | Avenqor Talent Search', 'Learn about Avenqor Talent Search — our mission, search philosophy, values and approach to executive search and talent advisory.');
  return <>
      <PageHero eyebrow="About Us" title="A considered approach to leadership search." intro="Avenqor Talent Search is an executive search and talent advisory firm focused on helping organizations identify and engage the professionals and leaders they need." data-id="element-374" data-mp="2dete909k3flm" />

      <section className="bg-white" aria-labelledby="mission-title" data-id="element-375" data-mp="2i7w5h1qv2cyn">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:py-32" data-id="element-376" data-mp="2w8708fvakbp3">
          <div className="relative lg:col-span-6" data-id="element-377" data-mp="2r9m0sinv5w1p">
            <div aria-hidden="true" className="absolute -bottom-3 -left-3 hidden h-full w-full border border-gold/50 sm:block" data-id="element-378" data-mp="1ouwfpibivtrt" />
            <img src={images.aboutConversation.src} alt={images.aboutConversation.alt} loading="lazy" className="relative aspect-[4/3] w-full object-cover" data-id="element-379" data-mp="1sx9ko4ig3nnj" />
          </div>
          <div className="lg:col-span-6" data-id="element-380" data-mp="22nvx5puv7y20">
            <SectionHeading id="mission-title" eyebrow="Our Mission" title="Clarity and confidence in every senior appointment." data-id="element-381" data-mp="27lxdw44tjlab" />
            <div className="mt-6 space-y-5 text-[17px] leading-relaxed text-ink" data-id="element-382" data-mp="2cinv4usd0mxb">
              <p data-id="element-383" data-mp="2gn3zgrmbrmaf">
                Our mission is to help organizations make leadership and specialist appointments with greater clarity
                and confidence — and to treat every professional we engage with as a respected peer.
              </p>
              <p className="text-muted" data-id="element-384" data-mp="r6w8r5bn23d9">
                We help organizations identify, engage, and connect with professionals whose expertise and leadership
                can support long-term success. That means combining disciplined research with discreet, informed
                conversations on both sides of a search.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-paper" aria-labelledby="philosophy-title" data-id="element-385" data-mp="4yqshictaxp6">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:py-32" data-id="element-386" data-mp="4we34k4q5l23">
          <div className="lg:col-span-4" data-id="element-387" data-mp="12jv5sj7vsll3">
            <SectionHeading id="philosophy-title" eyebrow="Search Philosophy" title="How we think about search." data-id="element-388" data-mp="xmv049ey1kui" />
          </div>
          <ul className="border-b border-navy/10 lg:col-span-7 lg:col-start-6" data-id="element-389" data-mp="smgpqkpvdytb">
            {philosophy.map(item => <li key={item.title} className="border-t border-navy/10 py-8" data-id="element-390" data-mp="oosqx6l23lw5">
                <h3 className="font-serif text-[1.7rem] font-medium leading-tight text-navy" data-id="element-391" data-mp="3dftu7lswdmvg">{item.title}</h3>
                <p className="mt-3 text-[16px] leading-relaxed text-muted" data-id="element-392" data-mp="39erw58apvgv2">{item.text}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-white" aria-labelledby="values-title" data-id="element-393" data-mp="15vqybj9ai65n">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-id="element-394" data-mp="r62zcookzjl3">
          <SectionHeading id="values-title" eyebrow="Our Values" title="What we hold ourselves to." intro="Executive search depends on trust from clients and candidates alike. These values guide how we earn it." data-id="element-395" data-mp="r3ykjbp59061" />
          <div className="mt-16" data-id="element-396" data-mp="1ot1n29xaph6u">
            <PrinciplesList items={values} columns={4} data-id="element-397" data-mp="1or79xhs9mtdb" />
          </div>
        </div>
      </section>

      <section className="bg-paper" aria-labelledby="about-approach-title" data-id="element-398" data-mp="1evdqrfk1k1mk">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-id="element-399" data-mp="1etkm25sznrh7">
          <SectionHeading id="about-approach-title" eyebrow="Our Approach" title="Four stages, one disciplined process." data-id="element-400" data-mp="3vj85grejvg32" />
          <div className="mt-16" data-id="element-401" data-mp="3s1ket059epw4">
            <ProcessSteps showDetail data-id="element-402" data-mp="3s0smpzavfaal" />
          </div>
        </div>
      </section>

      <CallToAction data-id="element-403" data-mp="3n1z5bvsgqz55" />
    </>;
}